import type { UserManagerSettings } from '@nrx/auth-ui';

/** Inputs for assembling FX portal OIDC settings (Vite env or tests). */
export interface FxPortalOidcEnv {
  /** FX partner Cognito user pool — not staff or provider pools. */
  readonly userPoolId?: string | undefined;
  readonly clientId?: string | undefined;
  readonly baseUrl?: string | undefined;
  readonly isDev?: boolean | undefined;
}

/**
 * Builds FX partner-pool OIDC settings for the FX portal SPA.
 *
 * @remarks Pure so unit tests can supply env without fighting Vite's
 * `import.meta.env` inlining.
 */
export function buildFxPortalAuthConfig(env: FxPortalOidcEnv): UserManagerSettings {
  const baseUrl = env.baseUrl ?? (env.isDev === true ? 'http://localhost:5180' : undefined);
  const region = env.userPoolId?.match(/^([a-z0-9-]+)_/)?.[1] ?? 'us-east-2';
  const authority = env.userPoolId
    ? `https://cognito-idp.${region}.amazonaws.com/${env.userPoolId}`
    : '';

  return {
    authority,
    client_id: env.clientId ?? '',
    redirect_uri: `${baseUrl ?? ''}/auth/callback`,
    post_logout_redirect_uri: `${baseUrl ?? ''}/auth/logout`,
    scope: 'openid profile email',
    response_type: 'code',
    automaticSilentRenew: true,
    loadUserInfo: true,
    monitorSession: true,
    validateSubOnSilentRenew: true,
  };
}

/** True when the minimum Cognito settings needed for OIDC sign-in are present. */
export function isFxPortalOidcConfigured(
  config: UserManagerSettings = fxPortalAuthConfig,
): boolean {
  return Boolean(config.authority && config.client_id && config.redirect_uri);
}

/** FX partner-pool OIDC configuration for the FX portal SPA (baked from Vite env). */
export const fxPortalAuthConfig: UserManagerSettings = buildFxPortalAuthConfig({
  userPoolId: import.meta.env['VITE_COGNITO_FX_USER_POOL_ID'] as string | undefined,
  clientId: import.meta.env['VITE_COGNITO_FX_CLIENT_ID'] as string | undefined,
  baseUrl: import.meta.env['VITE_FX_PORTAL_BASE_URL'] as string | undefined,
  isDev: import.meta.env.DEV,
});
